// FormBuilderBasicExtras — the type-specific Basic-options rows rendered in
// the slot after Placeholder (checkList's extras stay inline in
// FormBuilderBasicOptions): ratingSlider's "Rating scale" block (low/high
// label boxes around a −/+ count stepper, then icon tiles over a live preview
// — ratingIcon lives in basicProperties, not layoutProperties),
// numberSlider min/max, dateTime sub-type + Hijri toggle, number's
// English-numbers toggle, sendTo's send-via select, otp's type/country selects
// + SMS-message editor, and section repetition.
// dataSource adds three selects (data type · display option · source type) plus
// a Manage data source button opening DataSourceBindingDrawer, with a caption
// reading "<source name> · N bindings". companyField adds a company-field
// picker, a multi-select of display columns (the picked field's own column is
// locked on and always last), a display-option select, and an inline
// bindingList editor (CompanyFieldBindings — no drawer, unlike dataSource).
// Null for types without extras. Value sets confirmed against the app contract.

import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Button,
  Icon,
  Input,
  MentionTextarea,
  MultiSectionCheckboxDropdown,
  SelectDropdown,
  Switch,
} from '@/components/common';
import { useGetDataSourceSelectionQuery } from '@/api/endpoints/datasourceApi';
import { useGetCompanyFieldSelectionQuery } from '@/api/endpoints/companyFieldsApi';
import type { DataSourceBinding, ElementProperties } from './utils/formElementDefaults';
import type { BindableField, BuilderField } from './utils/builderModel';
import DataSourceBindingDrawer from './DataSourceBindingDrawer';
import CompanyFieldBindings from './CompanyFieldBindings';

// Confirmed value sets; labels use this repo's key pattern.
const DATE_TIME_TYPES = [
  { value: 'dateAndTime', labelKey: 'forms_builder_props_dt_date_time' },
  { value: 'date', labelKey: 'forms_builder_props_dt_date' },
  { value: 'time', labelKey: 'forms_builder_props_dt_time' },
];
const SEND_TO_TYPES = [
  { value: 'email', labelKey: 'forms_builder_props_sv_email' },
  { value: 'sms', labelKey: 'forms_builder_props_sv_sms' },
  { value: 'whatsapp', labelKey: 'forms_builder_props_sv_whatsapp' },
];
// otp value sets — confirmed against the app contract.
const OTP_SEND_VIA = [
  { value: 'email', labelKey: 'forms_builder_props_otp_sv_email' },
  { value: 'sms', labelKey: 'forms_builder_props_otp_sv_sms' },
  { value: 'whatsapp', labelKey: 'forms_builder_props_otp_sv_whatsapp' },
];
const OTP_COUNTRY_METHODS = [
  { value: 'by_ip_location', labelKey: 'forms_builder_props_otp_cm_ip_location' },
  { value: 'by_manual', labelKey: 'forms_builder_props_otp_cm_manual' },
];
// otp message variables — machine values confirmed. The display is a LITERAL, not
// a t() lookup: it becomes both the `#[Display]#` token text and the
// otpMessageContentMap key, i.e. it goes to the wire. Translating it would make
// the stored message and map differ per authoring locale.
const OTP_MENTIONS = [
  { value: 'user-name', display: 'User Name', icon: 'user' },
  { value: 'company-name', display: 'Company Name', icon: 'building' },
  { value: 'otp-code', display: 'OTP', icon: 'shield-check' },
];
// dataSource value sets — confirmed against the app contract (only `internal`
// exists so far; the Select stays for when external lands).
const DS_DATA_TYPES = [
  { value: 'text', labelKey: 'forms_builder_props_ds_dt_text' },
  { value: 'number', labelKey: 'forms_builder_props_ds_dt_number' },
];
const DS_DISPLAY_OPTIONS = [
  { value: 'open', labelKey: 'forms_builder_props_ds_do_open' },
  { value: 'dropdown', labelKey: 'forms_builder_props_ds_do_dropdown' },
  { value: 'auto-complete', labelKey: 'forms_builder_props_ds_do_autocomplete' },
];
const DS_TYPES = [{ value: 'internal', labelKey: 'forms_builder_props_ds_st_internal' }];
// companyField display options — confirmed set (no `open`, unlike dataSource).
const CF_DISPLAY_OPTIONS = [
  { value: 'dropdown', labelKey: 'forms_builder_props_cf_do_dropdown' },
  { value: 'auto-complete', labelKey: 'forms_builder_props_cf_do_autocomplete' },
];

/** `companyFieldDisplayColumns` invariant: the company field's own column is
 *  mandatory and always the LAST entry, whatever else is picked. */
const withOwnLast = (picked: string[], own: string) =>
  own ? [...picked.filter((c) => c !== own), own] : picked;

// Rating icons — `value` is the wire value, `icon` the lucide name (all four
// verified against the installed lucide-react). `smile` shipped in an earlier
// pass and is no longer offered; the canvas card still maps it so a design
// saved back then keeps rendering as drawn.
const RATING_ICONS = [
  { value: 'star', icon: 'star', labelKey: 'forms_builder_props_rate_ri_star' },
  { value: 'heart', icon: 'heart', labelKey: 'forms_builder_props_rate_ri_heart' },
  { value: 'ellipse', icon: 'circle', labelKey: 'forms_builder_props_rate_ri_ellipse' },
  { value: 'thumbsUp', icon: 'thumbs-up', labelKey: 'forms_builder_props_rate_ri_thumbsUp' },
];
/** Rating-count stepper bounds. */
const RATE_MIN = 1;
const RATE_MAX = 10;

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

interface FormBuilderBasicExtrasProps {
  field: BuilderField;
  onChange: (properties: ElementProperties) => void;
  /** Fields a dataSource binding can point at (empty for other types). */
  bindableFields?: BindableField[];
}

const FormBuilderBasicExtras = ({
  field,
  onChange,
  bindableFields = [],
}: FormBuilderBasicExtrasProps) => {
  const { t } = useTranslation();

  const bp = field.properties.basicProperties as Record<string, unknown>;

  // dataSource-only state/data. Hooks run for every element type, so both are
  // gated by `skip` / usage rather than by a branch.
  const [dsOpen, setDsOpen] = useState(false);
  const isDataSource = field.elementType === 'dataSource';
  const dataSourceId = (bp.dataSource as string) ?? '';
  const bindingList = (bp.bindingList as DataSourceBinding[] | undefined) ?? [];
  const { data: sources } = useGetDataSourceSelectionQuery(undefined, {
    skip: !isDataSource || !dataSourceId,
  });
  const sourceName = sources?.find((s) => s.id === dataSourceId)?.schemaName ?? '';

  // companyField-only data — same unconditional-hook + skip approach.
  const isCompanyField = field.elementType === 'companyField';
  const companyFieldId = (bp.companyFieldId as string) ?? '';
  const displayColumns = (bp.companyFieldDisplayColumns as string[] | undefined) ?? [];
  const { data: companyFields } = useGetCompanyFieldSelectionQuery(undefined, {
    skip: !isCompanyField,
  });
  const pickedCompanyField = companyFields?.find((c) => c.id === companyFieldId);
  // The chosen field names a data source; its columns come from the (cached)
  // data-source picker feed, which carries every file's column schema.
  const { data: cfSources } = useGetDataSourceSelectionQuery(undefined, {
    skip: !isCompanyField || !pickedCompanyField,
  });
  const cfColumns =
    cfSources?.find((s) => s.id === pickedCompanyField?.dataSourceId)?.columns ?? [];
  const ownColumnId = pickedCompanyField?.dataSourceColumnId ?? '';
  const setBasic = (patch: Record<string, unknown>) =>
    onChange({
      ...field.properties,
      basicProperties: { ...(field.properties.basicProperties as object), ...patch },
    } as ElementProperties);

  const numInput = (key: string, label: string, min?: number, max?: number) => (
    <Input
      size='sm'
      type='number'
      className='form-builder-props__med-num'
      value={String(bp[key] ?? '')}
      aria-label={label}
      onChange={(e) => {
        const n = Number(e.target.value);
        setBasic({ [key]: min !== undefined && max !== undefined ? clamp(n, min, max) : n });
      }}
    />
  );

  const selectRow = (
    key: string,
    labelKey: string,
    options: { value: string; labelKey: string }[],
    noteKey?: string,
  ) => (
    <div className='form-builder-props__lay-row'>
      {noteKey ? (
        <div className='form-builder-props__field-row'>
          <span className='form-builder-props__label'>{t(labelKey)}</span>
          <span className='form-builder-props__label-note'>{t(noteKey)}</span>
        </div>
      ) : (
        <span className='form-builder-props__label'>{t(labelKey)}</span>
      )}
      <SelectDropdown
        size='sm'
        aria-label={t(labelKey)}
        value={String(bp[key] ?? '')}
        options={options.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
        onChange={(next) => setBasic({ [key]: next })}
      />
    </div>
  );

  const toggleCard = (key: string, icon: string, labelKey: string) => (
    <div className='form-builder-props__toggle-card'>
      <span className='form-builder-props__toggle-icon'>
        <Icon name={icon} size={18} />
      </span>
      <span className='form-builder-props__toggle-text'>
        <span className='form-builder-props__toggle-title'>{t(labelKey)}</span>
        <span className='form-builder-props__toggle-desc'>{t(`${labelKey}_desc`)}</span>
      </span>
      <Switch
        checked={bp[key] === true}
        aria-label={t(labelKey)}
        onChange={(e) => setBasic({ [key]: e.target.checked })}
      />
    </div>
  );

  switch (field.elementType) {
    case 'ratingSlider': {
      // One "Rating scale" block per design: low label · count stepper · high
      // label · icon tiles · live preview. Everything writes to basicProperties
      // (ratingIcon moved here from layoutProperties).
      const count = clamp(Number(bp.ratingPointers ?? 5), RATE_MIN, RATE_MAX);
      const iconValue = (bp.ratingIcon as string) ?? 'star';
      const iconName = RATING_ICONS.find((o) => o.value === iconValue)?.icon ?? 'star';
      // The design shows the boxes carrying their own value ("Bad"/"Good") with
      // no label row, so the label copy becomes the accessible name.
      const labelInput = (key: 'lowLabel' | 'highLabel', labelKey: string) => (
        <Input
          size='sm'
          className='form-builder-props__rate-label'
          value={(bp[key] as string) ?? ''}
          aria-label={t(labelKey)}
          onChange={(e) => setBasic({ [key]: e.target.value })}
        />
      );

      return (
        <>
          <div className='form-builder-props__field-row'>
            <span className='form-builder-props__label'>{t('forms_builder_props_rate_scale')}</span>
          </div>
          <span className='form-builder-props__med-heading'>
            {t('forms_builder_props_rate_count')}
          </span>

          {labelInput('lowLabel', 'forms_builder_props_low_label')}

          <div
            className='form-builder-props__rate-stepper'
            role='group'
            aria-label={t('forms_builder_props_pointers')}
          >
            <button
              type='button'
              className='form-builder-props__rate-step'
              aria-label={t('forms_builder_props_rate_less')}
              disabled={count <= RATE_MIN}
              onClick={() => setBasic({ ratingPointers: count - 1 })}
            >
              <Icon name='minus' size={16} />
            </button>
            <span className='form-builder-props__rate-count'>{count}</span>
            <span className='form-builder-props__rate-mark' aria-hidden='true'>
              <Icon name={iconName} size={18} />
            </span>
            <button
              type='button'
              className='form-builder-props__rate-step'
              aria-label={t('forms_builder_props_rate_more')}
              disabled={count >= RATE_MAX}
              onClick={() => setBasic({ ratingPointers: count + 1 })}
            >
              <Icon name='plus' size={16} />
            </button>
          </div>

          {labelInput('highLabel', 'forms_builder_props_high_label')}

          <span className='form-builder-props__med-heading'>
            {t('forms_builder_props_rate_icon')}
          </span>
          <div
            className='form-builder-props__rate-tiles'
            role='group'
            aria-label={t('forms_builder_props_rate_icon')}
          >
            {RATING_ICONS.map((o) => (
              <button
                key={o.value}
                type='button'
                className={[
                  'form-builder-props__rate-tile',
                  o.value === iconValue && 'form-builder-props__rate-tile--active',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-label={t(o.labelKey)}
                aria-pressed={o.value === iconValue}
                onClick={() => setBasic({ ratingIcon: o.value })}
              >
                <Icon name={o.icon} size={20} />
              </button>
            ))}
          </div>
          <div className='form-builder-props__rate-preview' aria-hidden='true'>
            {Array.from({ length: count }, (_, i) => (
              <Icon key={i} name={iconName} size={18} />
            ))}
          </div>
        </>
      );
    }
    case 'numberSlider':
      return (
        <div className='form-builder-props__lay-row'>
          <span className='form-builder-props__label'>{t('forms_builder_props_range')}</span>
          <div className='form-builder-props__med-pair'>
            <span className='form-builder-props__med-unit'>
              {t('forms_builder_props_val_minRange')}
            </span>
            {numInput('minRange', t('forms_builder_props_val_minRange'))}
            <span className='form-builder-props__med-unit'>
              {t('forms_builder_props_val_maxRange')}
            </span>
            {numInput('maxRange', t('forms_builder_props_val_maxRange'))}
          </div>
        </div>
      );
    case 'dateTime':
      return (
        <>
          {selectRow('subType', 'forms_builder_props_dt_type', DATE_TIME_TYPES)}
          {toggleCard('showHijriDate', 'moon', 'forms_builder_props_hijri')}
        </>
      );
    case 'number':
      return toggleCard('allowEnglishNumbers', 'languages', 'forms_builder_props_english_numbers');
    case 'sendTo':
      return selectRow('sendToType', 'forms_builder_props_send_via', SEND_TO_TYPES);
    case 'otp':
      // Typing `#` in the message opens the variable menu; tokens are
      // `#[Display]#` and otpMessageContentMap (display → machine value) is
      // derived from the text by MentionTextarea on every change.
      return (
        <>
          {selectRow('otpSendVia', 'forms_builder_props_otp_type', OTP_SEND_VIA)}
          {selectRow(
            'countrySelectionMethod',
            'forms_builder_props_otp_country',
            OTP_COUNTRY_METHODS,
          )}
          <div className='form-builder-props__lay-row'>
            <span className='form-builder-props__label'>
              {t('forms_builder_props_otp_message')}
            </span>
            <MentionTextarea
              rows={4}
              value={(bp.otpMessageContent as string) ?? ''}
              mentionMap={(bp.otpMessageContentMap as Record<string, string>) ?? {}}
              options={OTP_MENTIONS}
              aria-label={t('forms_builder_props_otp_message')}
              onChangeValue={(next, map) =>
                setBasic({ otpMessageContent: next, otpMessageContentMap: map })
              }
            />
            <span className='form-builder-props__otp-hint'>
              <Trans i18nKey='forms_builder_props_otp_message_hint'>
                Type <strong>#</strong> to insert a variable
              </Trans>
            </span>
          </div>
        </>
      );
    case 'dataSource':
      return (
        <>
          {selectRow('dataType', 'forms_builder_props_ds_data_type', DS_DATA_TYPES)}
          {selectRow('subType', 'forms_builder_props_ds_display', DS_DISPLAY_OPTIONS)}
          {selectRow('dataSourceType', 'forms_builder_props_ds_type', DS_TYPES)}
          <Button
            variant='secondary'
            iconStart='settings-2'
            className='form-builder-props__ds-manage'
            onClick={() => setDsOpen(true)}
          >
            {t('forms_builder_props_ds_manage')}
          </Button>
          {sourceName && (
            <span className='form-builder-props__ds-caption'>
              {t('forms_builder_props_ds_caption', {
                name: sourceName,
                count: bindingList.length,
              })}
            </span>
          )}
          <DataSourceBindingDrawer
            open={dsOpen}
            onClose={() => setDsOpen(false)}
            dataSource={dataSourceId}
            displayColumn={(bp.displayColumn as string) ?? ''}
            valueColumn={(bp.valueColumn as string) ?? ''}
            bindingList={bindingList}
            bindableFields={bindableFields}
            onApply={setBasic}
          />
        </>
      );
    case 'companyField': {
      // The picked field's own column is mandatory, so it renders checked and
      // disabled; toggling any other column re-runs withOwnLast to keep it last.
      const toggleColumn = (columnId: string) => {
        if (columnId === ownColumnId) return; // locked
        const next = displayColumns.includes(columnId)
          ? displayColumns.filter((c) => c !== columnId)
          : [...displayColumns, columnId];
        setBasic({ companyFieldDisplayColumns: withOwnLast(next, ownColumnId) });
      };

      return (
        <>
          <div className='form-builder-props__lay-row'>
            <div className='form-builder-props__field-row'>
              <span className='form-builder-props__label'>{t('forms_builder_props_cf_field')}</span>
              <span className='form-builder-props__label-note'>
                {t('forms_builder_props_cf_field_note')}
              </span>
            </div>
            <SelectDropdown
              size='sm'
              aria-label={t('forms_builder_props_cf_field')}
              value={companyFieldId}
              options={[
                { value: '', label: t('forms_builder_props_cf_select') },
                ...(companyFields ?? []).map((c) => ({ value: c.id, label: c.fieldTitle })),
              ]}
              onChange={(id) => {
                const next = companyFields?.find((c) => c.id === id);
                // A new field means a different data source, so the old
                // columnIds are meaningless — reset to just its own column.
                setBasic({
                  companyFieldId: id,
                  companyFieldDisplayColumns: next?.dataSourceColumnId
                    ? [next.dataSourceColumnId]
                    : [],
                });
              }}
            />
          </div>

          <div className='form-builder-props__lay-row'>
            <div className='form-builder-props__field-row'>
              <span className='form-builder-props__label'>
                {t('forms_builder_props_cf_display_columns')}
              </span>
              <span className='form-builder-props__label-note'>
                {t('forms_builder_props_cf_display_columns_note')}
              </span>
            </div>
            <MultiSectionCheckboxDropdown
              label={t('forms_builder_props_cf_display_columns')}
              icon='columns-3'
              plainTrigger
              showCount
              portal
              width={240}
              emptyText={t('forms_builder_props_cf_columns_empty')}
              sections={[
                {
                  key: 'columns',
                  label: t('forms_builder_props_cf_display_columns'),
                  options: cfColumns.map((c) => ({
                    id: c.columnId,
                    label: c.columnName,
                    // The field's own column can't be turned off.
                    disabled: c.columnId === ownColumnId,
                  })),
                },
              ]}
              selected={{ columns: new Set(displayColumns) }}
              onToggle={(_section, id) => toggleColumn(id)}
            />
          </div>

          {selectRow('subType', 'forms_builder_props_cf_display', CF_DISPLAY_OPTIONS)}

          <CompanyFieldBindings
            bindings={bindingList}
            bindableFields={bindableFields}
            columns={cfColumns}
            onChange={(next) => setBasic({ bindingList: next })}
          />
        </>
      );
    }
    case 'section':
      return (
        <>
          {toggleCard('sectionRepeated', 'repeat', 'forms_builder_props_section_repeat')}
          {bp.sectionRepeated === true && (
            <div className='form-builder-props__med-sub'>
              <div className='form-builder-props__med-pair'>
                <span className='form-builder-props__med-unit'>
                  {t('forms_builder_props_val_minRange')}
                </span>
                {numInput('sectionMinRepetition', t('forms_builder_props_val_minRange'))}
                <span className='form-builder-props__med-unit'>
                  {t('forms_builder_props_val_maxRange')}
                </span>
                {numInput('sectionMaxRepetition', t('forms_builder_props_val_maxRange'))}
              </div>
            </div>
          )}
        </>
      );
    default:
      return null;
  }
};

export default FormBuilderBasicExtras;
